import React, { useMemo } from "react";
import type { ComponentType } from "react";

import { Error } from "@/pages";
import { createLegacyDynamicPageRouter } from "./pageRegistry/createLegacyDynamicPageRouter";
import { createLegacyPageRegistryHelpers } from "./pageRegistry/legacyPageRegistryHelpers";
import { createUseLegacyPageRouting } from "./pageRegistry/createUseLegacyPageRouting";
import type { LegacyPageConfig } from "./pageRegistry/legacyPageRegistryTypes";
import {
  useLegacyPageContext,
  useLegacyPageRegistry,
} from "./pageRegistry/useLegacyPageRegistry";
import FullPageLoading from "./components/FullPageLoading";

/**
 * LegacyDynamicPageRouter
 *
 * Substitui o antigo fluxo `usePages → CurrentPage`.
 *
 * Responsabilidades:
 * - centralizar loading de sessão
 * - centralizar guard global (termos/autorização)
 * - resolver e renderizar a página pelo param `:id` do legado
 * - fallback consistente para rota inválida (Error)
 */
export default function LegacyDynamicPageRouter() {
  const registry = useLegacyPageRegistry();
  const context = useLegacyPageContext();

  const helpers = useMemo(() => {
    return createLegacyPageRegistryHelpers(registry);
  }, [registry]);

  /**
   * Page default do legado:
   * - manter comportamento atual (renderiza a primeira página `initial` ativa
   *   quando não existe `:id`).
   * - fallback para "home".
   */
  const defaultPageName = useMemo(() => {
    const activePages = Object.values(registry).filter(
      (config) => config.meta?.isEnabled?.(context) ?? true,
    );

    const initialConfig = activePages.find(
      (config) => config.meta?.legacyFeature?.initial,
    );

    const initialPageName = initialConfig?.path?.startsWith("/")
      ? initialConfig.path.slice(1)
      : undefined;

    return initialPageName ?? "home";
  }, [context, registry]);

  const useLegacyPageRouting = useMemo(() => {
    return createUseLegacyPageRouting({
      getPageConfig: (pageName) => helpers.getPageConfig(pageName),
      isPageRegistered: (pageName) => helpers.isPageRegistered(pageName),
      defaultPageName,
      paramName: "id",
    });
  }, [defaultPageName, helpers]);

  /**
   * Adapter: converte o resultado do hook de routing (legado) para o contrato
   * esperado pelo `createLegacyDynamicPageRouter`.
   *
   * Importante:
   * - Mantemos como hook (`use*`) para respeitar Rules of Hooks e evitar
   *   falsos positivos de lint.
   */
  const useLegacyPageRoutingAdapter = () => {
    const routing = useLegacyPageRouting();

    return {
      currentPageName: routing.currentPageName,
      pageConfig: routing.pageConfig,
      PageComponent: routing.PageComponent as unknown as ComponentType | undefined,
      isValidPage: routing.isValidPage,
    };
  };

  const RouterComponent = useMemo(() => {
    return createLegacyDynamicPageRouter({
      usePageRouting: useLegacyPageRoutingAdapter,
      usePageContext: () => context,
      renderLoading: () => <FullPageLoading />,
      renderNotFound: () => <Error />,
      renderGlobalGuards: (pageContext) => {
        const person = pageContext.session?.person;
        const roles = pageContext.session?.roles;

        const hasTermRefused = person?.extension?.find((item: any) =>
          item.url.includes("termRefused"),
        )?.valueBoolean;

        const isAuthorizedRole = roles?.some(
          (role: any) => role.isOwner || role.isManager || role.isPractitioner,
        );

        const canNavigate =
          (hasTermRefused && isAuthorizedRole) || person?.active;

        if (canNavigate) {
          return null;
        }

        const termsConfig: LegacyPageConfig | undefined =
          helpers.getPageConfig("terms");

        const TermsComponent = termsConfig?.component;
        if (TermsComponent) {
          // Mantém comportamento do legado: renderiza Termos sem redirecionar.
          return <TermsComponent />;
        }

        return <Error />;
      },
    });
  }, [context, helpers, useLegacyPageRoutingAdapter]);

  return <RouterComponent />;
}

